import { hannWindow, magnitudeSpectrum } from '@/util/fft';

/**
 * The spectrum of what is playing, in bands the eye can read.
 *
 * A raw transform gives evenly spaced bins, which is the wrong shape for music:
 * half of them cover the top octave, where almost nothing distinguishable
 * happens, while the bottom three octaves share a handful. So the bins are
 * gathered into **logarithmically spaced bands** — equal musical intervals, equal
 * width on screen — and each band reports the total power of the bins inside it.
 *
 * Total, not average, and the difference is worth stating because it decides
 * what the display means. A band's total power is how much energy the music has
 * in that range, so a tone reads its own level wherever it happens to sit; a
 * band's *average* power would report the same tone as quieter the wider the
 * band, which is a fact about the display rather than about the music. The
 * consequence is that white noise slopes upwards by three decibels an octave —
 * which is correct, and is exactly why pink noise, not white, is what an
 * analyser is read flat against.
 *
 * Samples arrive from the same worklet tap the loudness meter uses, a block at a
 * time, and are kept in a ring buffer. The transform runs when the display asks
 * for it, not when audio arrives: a panel that is closed should cost nothing,
 * and a panel that is open needs a spectrum thirty times a second rather than
 * three hundred and seventy-five.
 */

/** Transform size. 4096 at 48kHz resolves about 12Hz, fine enough at the bottom. */
const FRAME = 4096;

/** The range worth showing. Below 20Hz is rumble; above 20kHz is nothing. */
export const MIN_FREQUENCY_HZ = 20;
export const MAX_FREQUENCY_HZ = 20000;

/** Noise power bandwidth of a Hann window, in bins. */
const WINDOW_ENBW_BINS = 1.5;

/** Anything below this reads as silence rather than as a very small number. */
export const FLOOR_DB = -100;

export interface SpectrumOptions {
  /** How many bands to report. */
  bands?: number;
  /**
   * How quickly a band may fall, in decibels per read. Rises are instant, so a
   * transient is seen; falls are slowed, so the display is readable rather than
   * flickering.
   */
  fallDbPerRead?: number;
}

const DEFAULT_BANDS = 48;
const DEFAULT_FALL_DB = 3;

export class SpectrumAnalyser {
  private readonly buffer = new Float32Array(FRAME);
  private writeIndex = 0;
  private filled = 0;
  private readonly window = hannWindow(FRAME);
  private readonly frame = new Float64Array(FRAME);
  private levels: Float32Array;
  private readonly bandCount: number;
  private readonly fallDb: number;
  /** First and last bin of each band, worked out once for this sample rate. */
  private bandBins: { from: number; to: number }[] = [];
  private preparedForRate = 0;

  constructor(
    private readonly sampleRate: number,
    options: SpectrumOptions = {},
  ) {
    this.bandCount = Math.max(4, Math.min(256, options.bands ?? DEFAULT_BANDS));
    this.fallDb = Math.max(0.1, options.fallDbPerRead ?? DEFAULT_FALL_DB);
    this.levels = new Float32Array(this.bandCount).fill(FLOOR_DB);
    this.prepareBands();
  }

  /** Frequency at the centre of each band, for labelling an axis. */
  bandFrequencies(): Float32Array {
    const out = new Float32Array(this.bandCount);
    for (let b = 0; b < this.bandCount; b++) {
      const lower = this.bandEdgeHz(b);
      const upper = this.bandEdgeHz(b + 1);
      out[b] = Math.sqrt(lower * upper);
    }
    return out;
  }

  /** Accepts a block of audio, summed to mono. */
  push(channels: readonly Float32Array[]): void {
    const length = channels[0]?.length ?? 0;
    if (length === 0 || channels.length === 0) return;
    const scale = 1 / channels.length;

    for (let i = 0; i < length; i++) {
      let sum = 0;
      for (const channel of channels) sum += channel[i];
      this.buffer[this.writeIndex] = sum * scale;
      this.writeIndex = (this.writeIndex + 1) % FRAME;
      if (this.filled < FRAME) this.filled++;
    }
  }

  /** Forgets everything, so a stopped transport shows nothing. */
  reset(): void {
    this.buffer.fill(0);
    this.writeIndex = 0;
    this.filled = 0;
    this.levels.fill(FLOOR_DB);
  }

  /**
   * The current spectrum, in decibels per band.
   *
   * The returned array is reused between calls — the caller draws it and does
   * not keep it — so a display running at sixty frames a second allocates
   * nothing.
   */
  read(): Float32Array {
    if (this.filled < FRAME) return this.levels;

    // Unwrap the ring into the analysis frame, oldest sample first, windowed.
    for (let i = 0; i < FRAME; i++) {
      this.frame[i] = this.buffer[(this.writeIndex + i) % FRAME] * this.window[i];
    }
    const magnitudes = magnitudeSpectrum(this.frame);

    // A Hann window halves the amplitude of what passes through it, and the
    // transform scales by the frame length; both are undone here so a full-scale
    // sine reads 0dB rather than some number that depends on the frame size.
    const normalise = 4 / FRAME;

    for (let b = 0; b < this.bandCount; b++) {
      const { from, to } = this.bandBins[b];
      let power = 0;
      for (let k = from; k <= to; k++) {
        const value = magnitudes[k] * normalise;
        power += value * value;
      }
      // Divided by the window's noise power bandwidth. A Hann window spreads a
      // tone across about one and a half bins, so summing the power in a band
      // counts it one and a half times; without this a full-scale sine reads
      // 1.76dB above full scale, which is a statement about the window rather
      // than about the audio.
      const db = power > 0 ? Math.max(FLOOR_DB, 10 * Math.log10(power / WINDOW_ENBW_BINS)) : FLOOR_DB;
      // Rise at once, fall slowly: a transient must be visible, but a display
      // that drops to the floor between frames is unreadable.
      this.levels[b] = db >= this.levels[b] ? db : Math.max(db, this.levels[b] - this.fallDb);
    }
    return this.levels;
  }

  private bandEdgeHz(index: number): number {
    const ratio = index / this.bandCount;
    return MIN_FREQUENCY_HZ * Math.pow(MAX_FREQUENCY_HZ / MIN_FREQUENCY_HZ, ratio);
  }

  private prepareBands(): void {
    if (this.preparedForRate === this.sampleRate) return;
    const bins = FRAME >> 1;
    const binHz = this.sampleRate / FRAME;
    this.bandBins = [];
    for (let b = 0; b < this.bandCount; b++) {
      const lower = this.bandEdgeHz(b);
      const upper = this.bandEdgeHz(b + 1);
      // At the bottom a band is narrower than one bin, so it takes the nearest
      // one rather than none: a band that reported silence because no bin centre
      // fell inside it would be a hole in the display, not a fact about the
      // audio.
      const from = Math.max(1, Math.min(bins - 1, Math.floor(lower / binHz)));
      const to = Math.max(from, Math.min(bins - 1, Math.ceil(upper / binHz) - 1));
      this.bandBins.push({ from, to });
    }
    this.preparedForRate = this.sampleRate;
  }
}
